import type { Capability, FabricResource } from "../../lib/schemas";
import { llmModelSchema, type LlmModel } from "./endpointConfiguration";

export interface ChannelModelSummary {
  active: number;
  pending: number;
}

/** Model capabilities discovered through one channel resource. */
export function channelModelCapabilities(resource: FabricResource): Capability[] {
  return resource.capabilities.filter((capability) => capability.kind === "model");
}

export function channelModelSummary(resource: FabricResource): ChannelModelSummary {
  const models = channelModelCapabilities(resource);
  return {
    active: models.filter((capability) => capability.enabled).length,
    pending: models.filter((capability) => !capability.enabled).length
  };
}

export function contractRecord(contract: unknown): Record<string, unknown> {
  return contract !== null && typeof contract === "object" && !Array.isArray(contract)
    ? (contract as Record<string, unknown>)
    : {};
}

export function capabilityUpstreamModel(capability: Capability): string {
  const contract = contractRecord(capability.contract);
  const upstream = contract.upstream_model;
  return typeof upstream === "string" && upstream.length > 0 ? upstream : capability.namespace;
}

export function capabilityEstimatedCost(capability: Capability): number {
  const contract = contractRecord(capability.contract);
  const cost = contract.estimated_cost_microunits;
  return typeof cost === "number" && Number.isFinite(cost) ? cost : 0;
}

export function capabilityLatencyMs(capability: Capability): number {
  const contract = contractRecord(capability.contract);
  const latency = contract.latency_ms;
  return typeof latency === "number" && Number.isFinite(latency) ? latency : 0;
}

export function discoveryModelDraft(capability: Capability): LlmModel {
  const contract = contractRecord(capability.contract);
  const capabilities = Array.isArray(contract.capabilities)
    ? contract.capabilities.filter((item): item is string => typeof item === "string")
    : ["text"];
  const parsed = llmModelSchema.safeParse({
    public_name: capabilityUpstreamModel(capability),
    upstream_name: capabilityUpstreamModel(capability),
    capabilities,
    input_cost_per_million: 0,
    output_cost_per_million: 0
  });
  if (parsed.success) return parsed.data;
  return {
    public_name: capabilityUpstreamModel(capability),
    upstream_name: capabilityUpstreamModel(capability),
    capabilities: ["text"],
    input_cost_per_million: 0,
    output_cost_per_million: 0
  };
}
